import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { shortLanguages } from 'services/locale';
import { CAPTCHA_COOLDOWN_STORAGE_KEY, clearCaptchaCooldownStorage } from './captchaStorage';

const CAPTCHA_LOGIN_ATTEMPTS_THRESHOLD = 5;
const CAPTCHA_FAILED_ATTEMPTS_LIMIT = 3;
const CAPTCHA_COOLDOWN_MS = 60 * 1000;

const getCooldownLeft = cooldownUntil => Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));

const getSavedCooldownUntil = () => {
  if (typeof window === 'undefined') return 0;

  const savedCooldownUntil = Number(localStorage.getItem(CAPTCHA_COOLDOWN_STORAGE_KEY));

  if (!savedCooldownUntil || savedCooldownUntil <= Date.now()) {
    localStorage.removeItem(CAPTCHA_COOLDOWN_STORAGE_KEY);
    return 0;
  }

  return savedCooldownUntil;
};

const useCaptcha = () => {
  const recaptchaRef = useRef(null);
  const loginFailedAttempts = useSelector(state => state.auth.loginFailedAttempts);
  const isAuth = useSelector(state => state.auth.isAuth);
  const lang = useSelector(state => state.locale.lang);
  const locale = useSelector(state => state.locale.locale);

  const [captchaToken, setCaptchaToken] = useState('');
  const [, setCaptchaFailedAttempts] = useState(0);
  const [captchaError, setCaptchaError] = useState('');
  const [cooldownUntil, setCooldownUntil] = useState(getSavedCooldownUntil);
  const [cooldownLeft, setCooldownLeft] = useState(() => getCooldownLeft(getSavedCooldownUntil()));

  const showCaptcha = loginFailedAttempts >= CAPTCHA_LOGIN_ATTEMPTS_THRESHOLD;
  const captchaLanguage = shortLanguages[lang] || shortLanguages['en-US'];
  const captchaErrorMessage = locale.captchaCouldNotBeVerified;
  const isCooldownActive = cooldownLeft > 0;
  const captchaMessage = isCooldownActive ? locale.captchaCooldownMessage(cooldownLeft) : captchaError;

  const resetCaptcha = useCallback(({ clearError = false } = {}) => {
    recaptchaRef.current?.reset();
    setCaptchaToken('');

    if (clearError) {
      setCaptchaError('');
    }
  }, []);

  const clearCooldown = useCallback(() => {
    clearCaptchaCooldownStorage();
    setCooldownUntil(0);
    setCooldownLeft(0);
  }, []);

  const handleCaptchaChange = value => {
    setCaptchaToken(value || '');

    if (value) {
      setCaptchaError('');
      setCaptchaFailedAttempts(0);
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken('');
  };

  const handleCaptchaErrored = () => {
    setCaptchaToken('');
    setCaptchaError(captchaErrorMessage);
  };

  const validateCaptchaBeforeLogin = useCallback(() => {
    if (!showCaptcha || captchaToken) {
      setCaptchaError('');
      return true;
    }

    if (isCooldownActive) return false;

    setCaptchaError(captchaErrorMessage);

    setCaptchaFailedAttempts(prev => {
      const nextFailedAttempts = prev + 1;

      if (nextFailedAttempts >= CAPTCHA_FAILED_ATTEMPTS_LIMIT) {
        const nextCooldownUntil = Date.now() + CAPTCHA_COOLDOWN_MS;

        if (typeof window !== 'undefined') {
          localStorage.setItem(CAPTCHA_COOLDOWN_STORAGE_KEY, String(nextCooldownUntil));
        }

        setCooldownUntil(nextCooldownUntil);
        setCooldownLeft(getCooldownLeft(nextCooldownUntil));

        return 0;
      }

      return nextFailedAttempts;
    });

    return false;
  }, [captchaErrorMessage, captchaToken, isCooldownActive, showCaptcha]);

  const handleLoginResult = useCallback(
    success => {
      if (success) {
        resetCaptcha({ clearError: true });
        setCaptchaFailedAttempts(0);
        clearCooldown();
        return;
      }

      if (!success && showCaptcha) {
        resetCaptcha({ clearError: true });
      }
    },
    [clearCooldown, resetCaptcha, showCaptcha],
  );

  useEffect(() => {
    if (!cooldownUntil) return undefined;

    const syncCooldown = () => {
      const nextCooldownLeft = getCooldownLeft(cooldownUntil);
      setCooldownLeft(nextCooldownLeft);

      if (nextCooldownLeft <= 0) {
        clearCooldown();
        setCaptchaFailedAttempts(0);
      }
    };

    syncCooldown();

    const interval = setInterval(syncCooldown, 1000);

    return () => clearInterval(interval);
  }, [clearCooldown, cooldownUntil]);

  useEffect(() => {
    if (isAuth) {
      resetCaptcha({ clearError: true });
      setCaptchaFailedAttempts(0);
      clearCooldown();
    }
  }, [clearCooldown, isAuth, resetCaptcha]);

  useEffect(() => {
    if (!showCaptcha) {
      resetCaptcha({ clearError: true });
      setCaptchaFailedAttempts(0);
    }
  }, [resetCaptcha, showCaptcha]);

  useEffect(() => {
    setCaptchaError(prev => (prev ? captchaErrorMessage : prev));
  }, [captchaErrorMessage]);

  useEffect(() => () => recaptchaRef.current?.reset(), []);

  return {
    values: {
      captcha: captchaToken,
      captchaLanguage,
      captchaMessage,
      showCaptcha,
      recaptchaRef,
      isCooldownActive,
    },
    handlers: {
      handleCaptchaChange,
      handleCaptchaErrored,
      handleCaptchaExpired,
      handleLoginResult,
      validateCaptchaBeforeLogin,
    },
  };
};

export default useCaptcha;
